class CartSizeSwitcher extends HTMLElement {
  // Class-scoped, not module-scoped: every asset here is a classic script sharing
  // one global lexical scope, so a top-level const collides across components.
  static CHEVRON_OPEN_CLASS = 'rotate-180';

  constructor() {
    super();
    this._isBound = false;
    this.activeIndex = -1;
    this.handleOutsideClick = null;
    this.handleMutationEnd = null;
  }

  // Queried at call time, never cached: Idiomorph replaces these children on
  // every repaint, so a cached reference would point at a detached node.
  get trigger() {
    return this.querySelector('[data-size-trigger]');
  }

  get listbox() {
    return this.querySelector('[data-size-listbox]');
  }

  get triggerLabel() {
    return this.querySelector('[data-size-trigger-label]');
  }

  get chevron() {
    return this.querySelector('.cart-size-switcher__chevron');
  }

  get options() {
    return [...this.querySelectorAll('[data-size-option]')];
  }

  connectedCallback() {
    // Bound unconditionally and guarded per interaction: a trigger that renders
    // disabled once must not leave the component inert for the life of the page,
    // and a re-attached root must not stack a second set of handlers.
    if (this._isBound) return;
    this._isBound = true;

    // Delegated on the root, which survives every morph — the children do not.
    this.addEventListener('click', (event) => {
      if (this.trigger?.disabled) return;

      if (event.target.closest('[data-size-trigger]')) {
        this.toggleOpen();
        return;
      }

      const option = event.target.closest('[data-size-option]');
      if (option) this.select(option);
    });

    this.addEventListener('keydown', (event) => {
      if (this.trigger?.disabled) return;

      if (event.target.closest('[data-size-trigger]')) {
        this.handleTriggerKeydown(event);
        return;
      }

      if (event.target.closest('[data-size-listbox]')) this.handleListboxKeydown(event);
    });

    this.handleOutsideClick = (event) => {
      if (!this.contains(event.target)) this.close({ returnFocus: false });
    };
    document.addEventListener('click', this.handleOutsideClick);

    // The repaint renders the listbox closed, so the tracked index must follow.
    this.handleMutationEnd = () => {
      this.activeIndex = -1;
    };
    document.addEventListener('cart:mutation-end', this.handleMutationEnd);
  }

  disconnectedCallback() {
    if (this.handleOutsideClick) {
      document.removeEventListener('click', this.handleOutsideClick);
    }
    if (this.handleMutationEnd) {
      document.removeEventListener('cart:mutation-end', this.handleMutationEnd);
    }
  }

  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.open();
      this.setActive(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
      this.setActive(this.options.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
    }
  }

  handleListboxKeydown(event) {
    const { length } = this.options;
    if (!length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActive((this.activeIndex + 1) % length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActive((this.activeIndex - 1 + length) % length);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(this.options[this.activeIndex]);
    } else if (event.key === 'Escape') {
      // The drawer listens on document — without this, Escape would also close it.
      event.stopPropagation();
      this.close({ returnFocus: true });
    } else if (event.key === 'Tab') {
      this.close({ returnFocus: false });
    }
  }

  open() {
    const { trigger, listbox } = this;
    if (!trigger || !listbox) return;

    listbox.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.chevron?.classList.add(CartSizeSwitcher.CHEVRON_OPEN_CLASS);

    const { options } = this;
    const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    if (options.length) this.setActive(selectedIndex >= 0 ? selectedIndex : 0);
  }

  close({ returnFocus = true } = {}) {
    const { trigger, listbox } = this;
    if (!trigger || !listbox) return;

    listbox.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    this.chevron?.classList.remove(CartSizeSwitcher.CHEVRON_OPEN_CLASS);

    if (this.activeIndex >= 0) {
      this.options[this.activeIndex]?.setAttribute('tabindex', '-1');
      this.activeIndex = -1;
    }

    if (returnFocus) trigger.focus();
  }

  toggleOpen() {
    if (this.listbox?.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  // Roving tabindex only. The active option's highlight is the `focus:` variant in
  // the template — a JS-toggled Tailwind class is never emitted by the JIT.
  setActive(index) {
    const { options } = this;
    const nextOption = options[index];
    if (!nextOption) return;

    if (this.activeIndex >= 0) options[this.activeIndex]?.setAttribute('tabindex', '-1');

    nextOption.setAttribute('tabindex', '0');
    nextOption.focus();

    this.activeIndex = index;
  }

  select(option) {
    if (!option) return;

    const { variantId } = option.dataset;
    const { lineKey, quantity, currentVariant } = this.dataset;

    if (variantId === currentVariant) {
      this.close();
      return;
    }

    for (const item of this.options) {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    }

    const { triggerLabel, trigger } = this;
    if (triggerLabel) triggerLabel.textContent = option.textContent.trim();

    // Zero the old line by key, never by variant id — a cart can hold the same
    // variant twice with different properties.
    const updates = { [lineKey]: 0, [variantId]: Number(quantity) };
    // A row merged from a discount split moves every line behind it.
    const mergedLines = this.closest('[data-item-key]')?.dataset.mergedLines ?? '';
    for (const entry of mergedLines.split(',').filter(Boolean)) {
      updates[entry.split('=')[0]] = 0;
    }

    // Focus returns to the trigger BEFORE the request, not after: hiding the listbox
    // while an option holds focus hands it to <body>, and _captureFocus only records
    // an element still inside the drawer.
    const sizeLabel = option.textContent.trim();
    this.close({ returnFocus: true });

    const host = this.closest('cart-drawer');

    if (host) {
      host.mutate((sections) => CartAPI.update({ updates }, sections), {
        control: trigger,
        lineKey,
        announce: host.announcement('announceSizeChanged', { '%size%': sizeLabel }),
      });
    } else {
      CartAPI.update({ updates }).then(() => window.location.reload());
    }
  }
}

if (!customElements.get('cart-size-switcher')) {
  customElements.define('cart-size-switcher', CartSizeSwitcher);
}
